// src/modules/app-views/products/pages/Document/tabs/SuppliersTab/modals/SupplierModal/Step1.jsx
// *****************************************************************************
// SUPPLIER MODAL - STEP 1 (ITEM)
// *****************************************************************************
// Supplier entity selection + product identification:
// Line 1: SKU* | Description* | Purchase Unit*
// Line 2: CFOP | Tax Situation | Warranty (days) | Delivery (days)
// Line 3: Notes (observation)
// *****************************************************************************

// *****************************************************************************
// IMPORTS
// *****************************************************************************
import { useEffect } from 'react';
import { Form, Row, Col } from 'antd';
import SimpleSelectAsyncEntities from 'components/SimpleComponents/SimpleSelectAsyncEntities';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleSwitch from 'components/SimpleComponents/SimpleSwitch';
import { openModal } from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import { CFOP_ENTRY_OPTIONS, ICMS_CST_OPTIONS } from 'modules/app-views/products/constants';
import ProductsParametersModal from 'modules/app-views/products/pages/Init/modals/ProductsParametersModal';
import { useAppStore } from 'stores/app';

// *****************************************************************************
// STEP CONTENT COMPONENT
// *****************************************************************************
const Step1Content = ({ editingSupplier }) => {
  // ---------------------------------------------------------------------------
  // ZUSTAND
  // ---------------------------------------------------------------------------
  const storedParameters = useZustand('app', 'parameters', false);
  const form = Form.useFormInstance();

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES
  // ---------------------------------------------------------------------------
  const units = storedParameters?.modules?.products?.units || [];
  const unitOptions = units.map(u => ({ label: u.name, value: u.uid }));

  // ---------------------------------------------------------------------------
  // EFFECTS
  // ---------------------------------------------------------------------------
  // Focus supplier selector on initial mount
  useEffect(() => {
    const timer = setTimeout(() => {
      const el = document.getElementById('supplierId');
      if (el) el.focus();
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------
  // Capture entity name when user selects a supplier
  const handleEntitySelect = entity => {
    if (entity) {
      form?.setFieldValue('_supplierName', entity.name || entity.fullName || '');
    }
  };

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* Entity selector */}
      <SimpleSelectAsyncEntities
        name="supplierId"
        label="supplier"
        required
        entityProfiles={['supplier']}
        placeholder="search_entity"
        onEntitySelect={handleEntitySelect}
        selectedEntity={
          editingSupplier
            ? {
                _id: editingSupplier.supplierId,
                name: editingSupplier.supplierName || editingSupplier.supplierId,
              }
            : null
        }
      />
      {/* Hidden field to store supplier name for later use */}
      <Form.Item name="_supplierName" hidden>
        <input type="hidden" />
      </Form.Item>

      {/* Row 1: SKU* | Description* | Purchase Unit* */}
      <Row gutter={[16, 0]}>
        <Col xs={12} sm={4}>
          <SimpleInput name="sku" label="supplier_sku" required />
        </Col>
        <Col xs={24} sm={14}>
          <SimpleInput name="description" label="description" required />
        </Col>
        <Col xs={12} sm={6}>
          <SimpleSelect
            name="unitUid"
            label="purchase_unit"
            options={unitOptions}
            required
            allowClear
            showAddButton
            onAddButtonFn={() => openModal(ProductsParametersModal, {
              initialTab: 'units',
              afterClose: () => useAppStore.getState().refreshParameters?.(),
            })}
          />
        </Col>
      </Row>

      {/* Row 2: CFOP | Tax Situation | Warranty (days) | Delivery (days) */}
      <Row gutter={[16, 0]}>
        <Col xs={12} sm={6}>
          <SimpleSelect
            name="cfop"
            label="cfop"
            showSearch
            allowClear
            options={CFOP_ENTRY_OPTIONS}
          />
        </Col>
        <Col xs={12} sm={6}>
          <SimpleSelect
            name="taxSituation"
            label="tax_situation"
            showSearch
            allowClear
            options={ICMS_CST_OPTIONS}
          />
        </Col>
        <Col xs={12} sm={6}>
          <SimpleNumber name="warrantyDays" label="warranty_days" min={0} />
        </Col>
        <Col xs={12} sm={6}>
          <SimpleNumber name="leadTimeDays" label="delivery_time_days" min={0} />
        </Col>
      </Row>

      {/* Row 3: Notes + Primary switch */}
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={18}>
          <SimpleInput name="notes" label="notes" />
        </Col>
        <Col xs={24} sm={6}>
          <SimpleSwitch name="isPrimary" label="primary_supplier" />
        </Col>
      </Row>
    </>
  );
};

// *****************************************************************************
// STEP CONFIGURATION
// *****************************************************************************
const step1 = ({ editingSupplier }) => ({
  stepTitle: 'item',
  validationFields: editingSupplier ? [] : ['supplierId', 'sku', 'description', 'unitUid'],
  content: <Step1Content editingSupplier={editingSupplier} />,
  onEnter: ({ goFocus }) => goFocus?.('supplierId'),
});

// *****************************************************************************
// EXPORT
// *****************************************************************************
export default step1;

// Named export for direct use in Tabs mode (edit)
export { Step1Content };
